import { useQuery, useMutation, useQueryClient } from 'react-query';
import { message } from 'antd';
import { partnersApi } from '../api';

// Query keys
export const PARTNERS_QUERY_KEY = 'partners';
export const PARTNER_DETAIL_QUERY_KEY = 'partner-detail';
export const PARTNER_STATS_QUERY_KEY = 'partner-stats';
export const PARTNER_TRANSACTIONS_QUERY_KEY = 'partner-transactions';

// Get all partners with pagination and filters
export const usePartners = (params = {}, options = {}) => {
  return useQuery(
    [PARTNERS_QUERY_KEY, params],
    () => partnersApi.getAll(params),
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
      ...options,
    }
  );
};

// Get single partner by ID
export const usePartner = (id, options = {}) => {
  return useQuery(
    [PARTNER_DETAIL_QUERY_KEY, id],
    () => partnersApi.getById(id),
    {
      enabled: !!id,
      staleTime: 30000,
      ...options,
    }
  );
};

// Get partner statistics
export const usePartnerStats = (id, options = {}) => {
  return useQuery(
    [PARTNER_STATS_QUERY_KEY, id],
    () => partnersApi.getStats(id),
    {
      enabled: !!id,
      staleTime: 60000, // 1 minute
      ...options,
    }
  );
};

// Get partner transactions
export const usePartnerTransactions = (id, params = {}, options = {}) => {
  return useQuery(
    [PARTNER_TRANSACTIONS_QUERY_KEY, id, params],
    () => partnersApi.getTransactions(id, params),
    {
      enabled: !!id,
      keepPreviousData: true,
      staleTime: 30000,
      ...options,
    }
  );
};

// Create partner mutation
export const useCreatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation(partnersApi.create, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(PARTNERS_QUERY_KEY);
      message.success('Partner created successfully');
      return data;
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to create partner';
      message.error(errorMessage);
    },
  });
};

// Update partner mutation
export const useUpdatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }) => partnersApi.update(id, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(PARTNERS_QUERY_KEY);
        queryClient.invalidateQueries([PARTNER_DETAIL_QUERY_KEY, variables.id]);
        message.success('Partner updated successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to update partner';
        message.error(errorMessage);
      },
    }
  );
};

// Delete partner mutation
export const useDeletePartner = () => {
  const queryClient = useQueryClient();

  return useMutation(partnersApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries(PARTNERS_QUERY_KEY);
      message.success('Partner deleted successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to delete partner';
      message.error(errorMessage);
    },
  });
};

// Upload partner logo mutation
export const useUploadPartnerLogo = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, file }) => partnersApi.uploadLogo(id, file),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([PARTNER_DETAIL_QUERY_KEY, variables.id]);
        queryClient.invalidateQueries(PARTNERS_QUERY_KEY);
        message.success('Logo uploaded successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to upload logo';
        message.error(errorMessage);
      },
    }
  );
};

// Update partner status mutation
export const useUpdatePartnerStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }) => partnersApi.updateStatus(id, status),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(PARTNERS_QUERY_KEY);
        queryClient.invalidateQueries([PARTNER_DETAIL_QUERY_KEY, variables.id]);
        message.success('Partner status updated successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to update status';
        message.error(errorMessage);
      },
    }
  );
};